"use client"

import * as React from "react"
import useEmblaCarousel from "embla-carousel-react"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "@/components/ui/utils"

type UseEmblaCarouselReturn = ReturnType<typeof useEmblaCarousel>

export type CarouselApi = UseEmblaCarouselReturn[1]

type CarouselContextValue = {
  carouselRef: UseEmblaCarouselReturn[0]
  api: CarouselApi
  scrollPrev: () => void
  scrollNext: () => void
  orientation: "horizontal" | "vertical"
}

const CarouselContext = React.createContext<CarouselContextValue | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)
  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />")
  }
  return context
}

export type CarouselProps = React.HTMLAttributes<HTMLDivElement> & {
  opts?: Parameters<typeof useEmblaCarousel>[0]
  plugins?: Parameters<typeof useEmblaCarousel>[1]
  orientation?: "horizontal" | "vertical"
  setApi?: (api: CarouselApi) => void
}

export function Carousel({
  orientation = "horizontal",
  opts,
  plugins,
  setApi,
  className,
  children,
  ...props
}: CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins,
  )

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev()
  }, [api])

  const scrollNext = React.useCallback(() => {
    api?.scrollNext()
  }, [api])

  React.useEffect(() => {
    if (!setApi) return
    setApi(api)
  }, [api, setApi])

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api,
        scrollPrev,
        scrollNext,
        orientation,
      }}
    >
      <div
        aria-roledescription="carousel"
        className={cn("relative overflow-hidden", className)}
        ref={carouselRef}
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

export function CarouselContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const { orientation } = useCarousel()

  return (
    <div
      className={cn(
        "flex",
        orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
        className,
      )}
      {...props}
    />
  )
}

export function CarouselItem({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const { orientation } = useCarousel()

  return (
    <div
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className,
      )}
      role="group"
      aria-roledescription="slide"
      {...props}
    />
  )
}

export function CarouselPrevious({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { orientation, scrollPrev } = useCarousel()

  return (
    <button
      aria-label="Previous slide"
      className={cn(
        "absolute inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(116,102,70,0.18)] bg-white/95 text-[hsl(var(--ink))] shadow-[0_8px_24px_hsl(var(--maroon)/0.08)] transition-all hover:-translate-y-px hover:bg-white hover:border-[hsl(var(--gold)/0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2",
        orientation === "horizontal"
          ? "left-4 top-1/2 -translate-y-1/2"
          : "left-1/2 top-4 -translate-x-1/2 rotate-90",
        className,
      )}
      onClick={scrollPrev}
      type="button"
      {...props}
    >
      <ChevronLeft className="h-5 w-5" />
    </button>
  )
}

export function CarouselNext({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { orientation, scrollNext } = useCarousel()

  return (
    <button
      aria-label="Next slide"
      className={cn(
        "absolute inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(116,102,70,0.18)] bg-white/95 text-[hsl(var(--ink))] shadow-[0_8px_24px_hsl(var(--maroon)/0.08)] transition-all hover:-translate-y-px hover:bg-white hover:border-[hsl(var(--gold)/0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2",
        orientation === "horizontal"
          ? "right-4 top-1/2 -translate-y-1/2"
          : "bottom-4 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      onClick={scrollNext}
      type="button"
      {...props}
    >
      <ChevronRight className="h-5 w-5" />
    </button>
  )
}
